import React, { useEffect, useRef, useState } from 'react';
// React and hooks
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { Alert, Button, Col, Form, Modal, Row, Space, Tabs, Typography } from 'antd';
import { CloseSquareOutlined, FileSearchOutlined } from '@ant-design/icons';
import { HasAccess, usePermify } from '@permify/react-role';
import { useSubscription } from '@apollo/react-hooks';

// Utils
import { formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { canEditCampaignOrder } from '../../Campaigns/shared/editAccess';

// Shared components
import OverlaySpinner from '../../../shared/OverlaySpinner';
import StatusTag, { getServedImpressions } from '../../Campaigns/shared/StatusTag';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { useCampaignOrderData } from '../../CampaignRequestor/hooks/useCampaignOrderData';

// GraphQL
import CAMPAIGN_ORDER_SUBSCRIPTION from '../../../../core/GraphQl/Subscriptions/CAMPAIGN_ORDER_SUBSCRIPTION';

// Local components
import { CampaignOrderQueueReviewDetails } from './CampaignOrderQueueReviewDetails';
import { CampaignOrderQueueReviewSummary } from './CampaignOrderQueueReviewSummary';
import { CampaignOrderQueueHistory } from './CampaignOrderQueueHistory';
import { CampaignLaunchDetailsProgress } from './CampaignLaunchDetailsProgress';
import { CampaignPendingDetailsProgress } from './CampaignPendingDetailsProgress';
import { CampaignOrderQueueCampaignsSummary } from './CampaignOrderQueueCampaignsSummary';

// Styles
import '../../CampaignRequestor/styles.css';
import './styles.css';
import { CampaignOrderQueueCampaignReview } from './CampaignOrderQueueCampaignReview';

const { Text } = Typography;

export const CampaignOrderQueueReview = () => {
  const { id } = useParams(); // Extract the ID from the URL
  const history = useHistory();
  const [campaignOrderReview, setCampaignOrderReview] = useState(null);
  const [form] = Form.useForm();
  const [showApprovedDialogSuccess, setShowApprovedDialogSuccess] = useState(false);
  const { timezone } = useTimezone();

  const { fetchCampaignOrder, currentCampaignOrder, loadingChildrenOrgs, loadingCampaignOrder } =
    useCampaignRequestorContext();

  useEffect(() => {
    if (id) {
      fetchCampaignOrder({
        variables: {
          where: {
            id,
          },
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data-readiness gate for queue/launch flow
  const { isReady: isDataReady } = useCampaignOrderData(currentCampaignOrder, {
    mode: 'queue',
  });

  // Post-launch edit access is processor-only (shared/editAccess.js)
  const { isAuthorized } = usePermify();
  const [isProcessor, setIsProcessor] = useState(false);
  useEffect(() => {
    const checkPermission = async () => {
      setIsProcessor(await isAuthorized(['isProcessor'], []));
    };
    checkPermission();
  }, [isAuthorized]);

  useEffect(() => {
    if (currentCampaignOrder && isDataReady) {
      setCampaignOrderReview(currentCampaignOrder);
      const values = {
        name: currentCampaignOrder.name,
        campaigns: currentCampaignOrder.campaigns.map((campaign) => ({
          id: campaign.id,
          name: campaign.name,
          demandSidePlatform: campaign.demandSidePlatform || '',
          adProviderId: campaign.adProviderId || '',
          maxBid: campaign.maxBid || 4.00,
          lineItems: (campaign.lineItems || []).map((lineItem) => ({
            id: lineItem.id,
            supplySidePlatform: lineItem.supplySidePlatform || '',
            maxBid: lineItem.maxBid || 4,
            frequencyCap: lineItem.frequencyCap || 4,
            dealIds: lineItem.dealIds || [],
          })),
        })),
        dspConfig: currentCampaignOrder.dspConfig || {},
      };

      form.setFieldsValue(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCampaignOrder, isDataReady]);

  // Subscribe to real-time campaign order updates so the sequential launch
  // guard clears automatically when a sibling campaign finishes launching.
  const { data: subscriptionData } = useSubscription(
    CAMPAIGN_ORDER_SUBSCRIPTION,
    {
      variables: {
        where: {
          mutation_in: ['UPDATED'],
          node: {
            id_in: id ? [id] : [],
          },
        },
      },
      skip: !id,
    }
  );

  // Track previous campaign statuses to detect launches completing
  const prevCampaignStatusesRef = useRef({});

  useEffect(() => {
    if (!subscriptionData?.campaignOrder?.node) return;
    const updated = subscriptionData.campaignOrder.node;

    // Only refetch if a campaign status actually changed
    const prevStatuses = prevCampaignStatusesRef.current;
    const currentStatus = updated.status;
    if (prevStatuses[updated.id] !== currentStatus) {
      prevCampaignStatusesRef.current[updated.id] = currentStatus;
      fetchCampaignOrder({ variables: { where: { id } } });
    }
  }, [subscriptionData, fetchCampaignOrder, id]);

  if (loadingChildrenOrgs || loadingCampaignOrder || !isDataReady) {
    return <OverlaySpinner />;
  }

  // Block access to archived campaigns - matches CampaignDetailsLoader.js pattern
  if (currentCampaignOrder && currentCampaignOrder.status === 'ARCHIVED') {
    return <Redirect to="/campaigns/main/1" />;
  }
  
  // Also check campaignOrderReview for archived status (fallback)
  if (campaignOrderReview && campaignOrderReview.status === 'ARCHIVED') {
    return <Redirect to="/campaigns/main/1" />;
  }

  const handleEditCampaign = () => {
    history.push(`/campaigns/requester/edit/${id}`);
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleMediaStatusUpdate = (campaignIndex, lineItemIndex, adGroupCreativeIndex, updates) => {
    setCampaignOrderReview(prevState => {
      const newState = { ...prevState };
      const campaign = { ...newState.campaigns[campaignIndex] };
      const lineItem = { ...campaign.lineItems[lineItemIndex] };
      const adGroup = { ...lineItem.adGroup };
      const adGroupCreative = { ...adGroup.adGroupCreatives[adGroupCreativeIndex] };

      adGroupCreative.medias = adGroupCreative.medias.map(media => {
        const update = updates.find(u => u.id === media.id);
        return update ? { ...media, ...update } : media;
      });

      adGroup.adGroupCreatives = [...adGroup.adGroupCreatives];
      adGroup.adGroupCreatives[adGroupCreativeIndex] = adGroupCreative;
      lineItem.adGroup = adGroup;
      campaign.lineItems = [...campaign.lineItems];
      campaign.lineItems[lineItemIndex] = lineItem;
      newState.campaigns = [...newState.campaigns];
      newState.campaigns[campaignIndex] = campaign;

      return newState;
    });
  };

  // Refetch campaign order data
  const refetchCampaignOrder = async () => {
    if (id) {
      await fetchCampaignOrder({
        variables: {
          where: {
            id,
          },
        },
      });
    }
  };

  return (
    <>
      {campaignOrderReview && (
        <>
          <Row style={{ backgroundColor: 'white', padding: '24px 24px 8px 24px' }}>
            <Col span={16}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Space direction="horizontal" size="large">
                  <FileSearchOutlined style={{ fontSize: '24px' }} />
                  <HasAccess permissions={['default', 'defaultSSO']}>
                    <StatusTag
                      startDate={
                        campaignOrderReview.startDate
                          ? campaignOrderReview.startDate
                          : new Date().toString()
                      }
                      endDate={
                        campaignOrderReview.endDate
                          ? campaignOrderReview.endDate
                          : new Date().toString()
                      }
                      statusCampaign={campaignOrderReview.status && campaignOrderReview.status}
                      attributionWindow={
                        campaignOrderReview.attributionWindow
                          ? campaignOrderReview.attributionWindow
                          : 'N/A'
                      }
                      impressions={getServedImpressions(campaignOrderReview)}
                    />
                  </HasAccess>
                  <h3>{formatCampaignName(campaignOrderReview, timezone)}</h3>
                </Space>
              </div>
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <Space direction="horizontal" size="small">
                {canEditCampaignOrder(campaignOrderReview?.status || 'DRAFT', { isProcessor }) && (
                  <Button onClick={handleEditCampaign}>Edit Campaign</Button>
                )}
                <Button onClick={handleBack}>Back</Button>
              </Space>
            </Col>
          </Row>
          <Tabs
            style={{ padding: '8px 24px' }}
            defaultActiveKey="1"
            className="campaign-order-queue-tabs"
          >
            <Tabs.TabPane tab="Details" key="1">
              <div style={{ paddingTop: 38 }}>
                <Row gutter={20}>
                  <Col span={18}>
                    <Form
                      form={form}
                      direction="vertical"
                      initialValues={{
                        name: '',
                        campaigns: [
                          {
                            id: '',
                            name: '',
                            demandSidePlatform: '',
                            supplySidePlatform: '',
                            adProviderId: '',
                            maxBid: 4.00,
                            lineItems: [
                              {
                                id: '',
                                maxBid: 0,
                                frequencyCap: 0,
                                dealIds: [],
                              },
                            ],
                          },
                        ],
                      }}
                    >
                      <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {campaignOrderReview.dspConfig &&
                          campaignOrderReview.dspConfig.status === 'ERROR' && (
                            <Alert
                              message={campaignOrderReview.dspConfig.status}
                              description={
                                campaignOrderReview.dspConfig.error ||
                                'No error message provided. Please contact support.'
                              }
                              type="error"
                              closable={{
                                'aria-label': 'close',
                                closeIcon: <CloseSquareOutlined />,
                              }}
                            />
                          )}
                        <CampaignOrderQueueReviewDetails
                          currentCampaignOrder={campaignOrderReview}
                        />

                        {currentCampaignOrder && (
                          <CampaignOrderQueueCampaignsSummary
                            campaigns={currentCampaignOrder.campaigns}
                            form={form}
                            currentCampaignOrder={currentCampaignOrder}
                          />
                        )}

                        <Form.List name="campaigns">
                          {(fields) => (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                              {fields.map(({ key, name, fieldKey, ...restField }, index) => (
                                <CampaignOrderQueueCampaignReview
                                  key={key}
                                  form={form}
                                  restField={restField}
                                  fieldName={name}
                                  fieldKey={fieldKey}
                                  campaignIndex={index}
                                  onMediaStatusUpdate={handleMediaStatusUpdate}
                                  refetchCampaignOrder={refetchCampaignOrder}
                                  campaignOrderData={campaignOrderReview}
                                />
                              ))}
                            </Space>
                          )}
                        </Form.List>
                      </Space>
                    </Form>
                  </Col>
                  {currentCampaignOrder &&
                    ['PENDING', 'LIVE_PENDING', 'PAUSED_PENDING', 'REJECTED'].includes(currentCampaignOrder.status) && (
                      <Col span={6}>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                          <CampaignOrderQueueReviewSummary
                            currentCampaignOrder={campaignOrderReview}
                            setShowApprovedDialogSuccess={setShowApprovedDialogSuccess}
                          />
                          <CampaignPendingDetailsProgress
                            campaigns={campaignOrderReview.campaigns}
                            form={form}
                          />
                        </Space>
                      </Col>
                    )}
                  {currentCampaignOrder &&
                    ['APPROVED', 'LIVE_APPROVED', 'PAUSED_APPROVED'].includes(currentCampaignOrder.status) && (
                      <Col span={6}>
                        <CampaignLaunchDetailsProgress
                          campaigns={campaignOrderReview.campaigns}
                          form={form}
                        />
                      </Col>
                    )}
                </Row>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="History" key="2">
              <div style={{ paddingTop: 38 }}>
                <CampaignOrderQueueHistory currentCampaignOrder={campaignOrderReview} />
              </div>
            </Tabs.TabPane>
          </Tabs>
        </>
      )}

      {showApprovedDialogSuccess && (
        <Modal
          visible={showApprovedDialogSuccess}
          title="CAMPAIGN APPROVED"
          onCancel={() => setShowApprovedDialogSuccess(false)}
          footer={[
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                setShowApprovedDialogSuccess(false);
                // Redirect to campaign order queue ready tab
                history.push('/campaigns/v2/orderqueue/ready-to-launched');
              }}
            >
              Okay
            </Button>,
          ]}
        >
          <Text>This campaign has been approved and is now ready to launch.</Text>
        </Modal>
      )}
    </>
  );
};
